import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { 
  Search, 
  Plus, 
  Edit2, 
  Calendar,
  CheckCircle,
  XCircle,
  X,
  AlertCircle,
  Clock,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Book,
  ArrowLeft
} from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const useInitialData = (currentPage, pageSize) => {
  const [data, setData] = useState({
    semestres: [],
    anneeEnCours: null,
    totalPages: 1,
    totalItems: 0,
    loading: false
  });
  const isInitialized = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, loading: true }));
      const [anneeResponse, semestresResponse] = await Promise.all([
        api.get('/etablissement/annees-academiques/'),
        api.get(`/emplois/liste-semestres/?page=${currentPage}&page_size=${pageSize}`)
      ]);

      const anneeActive = anneeResponse.data.find(annee => annee.status === true);
      const semestresData = semestresResponse.data.results || semestresResponse.data;
      const totalItems = semestresResponse.data.count || semestresResponse.data.length;

      setData({
        semestres: semestresData,
        anneeEnCours: anneeActive,
        totalPages: Math.ceil(totalItems / pageSize),
        totalItems,
        loading: false
      });

      if (!anneeActive) {
        toast.warning('Aucune année académique active. Veuillez d\'abord activer une année académique.');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
      setData(prev => ({ ...prev, loading: false }));
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      fetchData();
    }
  }, [fetchData]);

  return {
    ...data,
    fetchData
  };
};

const ListeSemestrePage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSemestre, setSelectedSemestre] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [loading, setLoading] = useState(false);

  const { semestres, anneeEnCours, totalPages, totalItems, loading: dataLoading, fetchData } = useInitialData(currentPage, pageSize);

  const [formData, setFormData] = useState({
    libelle: '',
    date_debut: '',
    date_fin: '',
    annee: ''
  });

  // Filtrer les semestres en fonction du terme de recherche
  const filteredSemestres = semestres.filter(semestre =>
    semestre.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mettre à jour formData quand anneeEnCours change
  useEffect(() => {
    if (anneeEnCours) {
      setFormData(prev => ({ ...prev, annee: anneeEnCours.id }));
    }
  }, [anneeEnCours]);

  const validatePeriod = (startDate, endDate, currentSemestreId = null) => {
    // Convertir les dates en objets Date pour la comparaison
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Vérifier que la date de fin est après la date de début
    if (end <= start) {
      toast.error('La date de fin doit être postérieure à la date de début');
      return false;
    }

    // Vérifier le chevauchement avec d'autres semestres
    const overlap = semestres.some(semestre => {
      // Ignorer le semestre en cours de modification
      if (currentSemestreId && semestre.id === currentSemestreId) {
        return false;
      }

      const semestreStart = new Date(semestre.date_debut);
      const semestreEnd = new Date(semestre.date_fin);

      // Vérifier si les périodes se chevauchent
      return (
        (start >= semestreStart && start <= semestreEnd) || // La nouvelle date de début est dans une période existante
        (end >= semestreStart && end <= semestreEnd) || // La nouvelle date de fin est dans une période existante
        (start <= semestreStart && end >= semestreEnd) // La nouvelle période englobe une période existante
      );
    });

    if (overlap) {
      toast.error('Cette période chevauche un autre semestre existant');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.annee) {
      toast.error('Aucune année académique en cours. Veuillez d\'abord activer une année académique.');
      return;
    }

    // Validation des dates
    if (!validatePeriod(formData.date_debut, formData.date_fin, selectedSemestre?.id)) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        annee: anneeEnCours.id
      };

      if (selectedSemestre) {
        await api.put(`/emplois/details-semestres/${selectedSemestre.id}/`, dataToSend);
        toast.success('Semestre mis à jour avec succès');
      } else {
        await api.post('/emplois/liste-semestres/', dataToSend);
        toast.success('Semestre ajouté avec succès');
      }
      setIsModalOpen(false);
      setFormData({
        libelle: '',
        date_debut: '',
        date_fin: '',
        annee: anneeEnCours?.id || ''
      });
      setSelectedSemestre(null);
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Une erreur est survenue';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (semestre) => {
    try {
      setLoading(true);
      await api.post(`/emplois/status-semestres/${semestre.id}/activer/`);
      toast.success('Semestre activé avec succès');
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'activation du semestre:', error);
      
      // Afficher le message d'erreur du backend
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          toast.error(errorData.message);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else {
          toast.error('Erreur lors de l\'activation du semestre');
        }
      } else {
        toast.error('Erreur lors de l\'activation du semestre');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (semestre) => {
    try {
      setLoading(true);
      await api.post(`/emplois/cloturer-semestres/${semestre.id}/cloturer/`);
      toast.success('Semestre clôturé avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la clôture du semestre');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Si les deux dates sont remplies, valider la période
    if (name === 'date_debut' && formData.date_fin) {
      validatePeriod(value, formData.date_fin, selectedSemestre?.id);
    } else if (name === 'date_fin' && formData.date_debut) {
      validatePeriod(formData.date_debut, value, selectedSemestre?.id);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Trimestres</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les trimestres et leurs périodes
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Contenu existant */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Liste des trimestres</h2>
                    <p className="text-sm text-gray-600">
                      {anneeEnCours ? `Année académique : ${anneeEnCours.libelle}` : 'Aucune année académique active'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!anneeEnCours) {
                        toast.warning('Veuillez d\'abord activer une année académique');
                        return;
                      }
                      setSelectedSemestre(null);
                      setFormData({ libelle: '', date_debut: '', date_fin: '', annee: anneeEnCours?.id || '' });
                      setIsModalOpen(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Ajouter un trimestre
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      placeholder="Rechercher..."
                    />
                  </div>
                </div>

                {loading && semestres.length === 0 ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                  </div>
                ) : semestres.length === 0 ? (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
                    <Book className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun trimestre disponible</h3>
                    <p className="text-gray-500 mb-4">Commencez par ajouter un trimestre.</p>
                    <button
                      onClick={() => {
                        setSelectedSemestre(null);
                        setFormData({ libelle: '', date_debut: '', date_fin: '', annee: anneeEnCours?.id || '' });
                        setIsModalOpen(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter un trimestre
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 p-4">
                        {filteredSemestres.map((semestre) => (
                          <div
                            key={semestre.id}
                            className="relative bg-white border rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-lg font-medium text-gray-900">{semestre.libelle}</h3>
                                <p className="text-sm text-gray-500">{semestre.annee?.libelle}</p>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedSemestre(semestre);
                                    setFormData({
                                      libelle: semestre.libelle,
                                      date_debut: semestre.date_debut,
                                      date_fin: semestre.date_fin,
                                      annee: semestre.annee?.id
                                    });
                                    setIsModalOpen(true);
                                  }}
                                  className="text-[#202654] hover:text-[#202654]/80"
                                >
                                  <Edit2 className="h-5 w-5" />
                                </button>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="h-4 w-4 mr-2" />
                                <span>Début: {new Date(semestre.date_debut).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="h-4 w-4 mr-2" />
                                <span>Fin: {new Date(semestre.date_fin).toLocaleDateString()}</span>
                              </div>
                            </div>

                            <div className="mt-4 flex justify-between items-center">
                              <div className="flex items-center">
                                {semestre.status ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <Clock className="h-4 w-4 mr-1" />
                                    En cours
                                  </span>
                                ) : semestre.cloture ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    <CalendarCheck className="h-4 w-4 mr-1" />
                                    Clôturé
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <Clock className="h-4 w-4 mr-1" />
                                    En attente
                                  </span>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                {!semestre.status && !semestre.cloture && (
                                  <button
                                    onClick={() => handleActivate(semestre)}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Activer
                                  </button>
                                )}
                                {semestre.status && !semestre.cloture && (
                                  <button
                                    onClick={() => handleClose(semestre)}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Clôturer
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6 mt-4">
                      <div className="flex justify-between flex-1 sm:hidden">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Précédent
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Suivant
                        </button>
                      </div>
                      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Affichage de{' '}
                            <span className="font-medium">
                              {((currentPage - 1) * pageSize) + 1}
                            </span>{' '}
                            à{' '}
                            <span className="font-medium">
                              {Math.min(currentPage * pageSize, totalItems)}
                            </span>{' '}
                            sur{' '}
                            <span className="font-medium">{totalItems}</span> trimestres
                          </p>
                        </div>
                        <div>
                          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                                currentPage === 1 
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <span className="sr-only">Précédent</span>
                              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                              <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  currentPage === i + 1
                                    ? 'z-10 bg-[#202654] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#202654]'
                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                                currentPage === totalPages 
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <span className="sr-only">Suivant</span>
                              <ChevronRight className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal existant */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedSemestre ? 'Modifier le trimestre' : 'Ajouter un trimestre'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setSelectedSemestre(null);
                      setFormData({
                        libelle: '',
                        date_debut: '',
                        date_fin: '',
                        annee: anneeEnCours?.id || ''
                      });
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="mt-4">
                  <input
                    type="hidden"
                    name="annee"
                    value={anneeEnCours?.id || ''}
                  />

                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Année Académique
                    </label>
                    <p className="text-sm text-gray-900">
                      {anneeEnCours ? anneeEnCours.libelle : 'Aucune année académique active'}
                    </p>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="libelle" className="block text-sm font-medium text-gray-700 mb-1">
                      Libellé
                    </label>
                    <input
                      type="text"
                      name="libelle"
                      id="libelle"
                      value={formData.libelle}
                      onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="date_debut" className="block text-sm font-medium text-gray-700 mb-1">
                      Date de début
                    </label>
                    <input
                      type="date"
                      name="date_debut"
                      id="date_debut"
                      value={formData.date_debut}
                      onChange={handleDateChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="date_fin" className="block text-sm font-medium text-gray-700 mb-1">
                      Date de fin
                    </label>
                    <input
                      type="date"
                      name="date_fin"
                      id="date_fin"
                      value={formData.date_fin}
                      onChange={handleDateChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      required
                    />
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={loading || !anneeEnCours}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enregistrement...
                        </>
                      ) : (
                        selectedSemestre ? 'Modifier' : 'Ajouter'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        setSelectedSemestre(null);
                        setFormData({
                          libelle: '',
                          date_debut: '',
                          date_fin: '',
                          annee: anneeEnCours?.id || ''
                        });
                      }}
                      className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ListeSemestrePage; 